import { S25Util } from "../../../../util/s25-util";
import { EventService, Etags } from "../../../../services/event.service";
import { S25ProfileUtil } from "../../utils/s25.profile.util";
import { ReservationService } from "../../../../services/reservation.service";
import { WsDataI, EventMicroI, EventDataMicroI, WSReservations } from "../../EventMicroI";
import { ObjectSearchAvail } from "../../../s25-object-search/object.search.avail";
import { AvailLocationItems, AvailResourceItems } from "../../../s25-object-search/s25.object.search.util";
import {
    S25Reservation,
    S25ObjectReservation,
    S25RsReservation,
    S25RmReservation,
    ObjectType,
} from "../../ReservationI";
import { S25Event } from "../../EventMicroI";
import { S25Profile, Ids, RecordType, AddedTime } from "../../ProfileI";
import { EventSummaryService } from "../../../s25-swarm-schedule/s25.event.summary.service";
import { BOARD_CONST } from "../../../s25-swarm-schedule/s25.board.const";
import { Proto } from "../../../../pojo/Proto";
import DowNumber = Proto.DowNumber;

declare global {
    interface Window {
        angBridge: any;
    }
}

export type CopyOccTarget = {
    profileId: number;
    rsrvId: number;
    keepRepeatPattern?: boolean;
};

export class EventOccUtil {
    public static async copyOcc(occ: S25Reservation) {
        const newRsrvId: any = await ReservationService.generateReservationIds(1);
        const copyOcc = S25Util.deepCopy(occ) as S25Reservation;
        copyOcc.itemId = newRsrvId.idSet[0].rsrvId;
        copyOcc.state = 1;
        return copyOcc;
    }

    public static async createNewOcc(profile: S25Profile, startDate?: Date, endDate?: Date): Promise<S25Reservation> {
        const reservations = (profile.reservations ?? []) as S25Reservation[];
        let eventStart: Date;
        let eventEnd: Date;

        if (startDate && endDate) {
            eventStart = startDate;
            eventEnd = endDate;
        } else if (reservations.length > 0) {
            const sorted = [...reservations].sort(
                (first, second) =>
                    new Date(second.eventStart ?? 0).getTime() - new Date(first.eventStart ?? 0).getTime(),
            );
            const lastOcc = sorted[0];
            const lastStart = lastOcc.eventStart ?? new Date();
            const lastEnd = lastOcc.eventEnd ?? lastStart;
            eventStart = S25Util.date.addDays(new Date(lastStart), 1);
            eventEnd = S25Util.date.addDays(new Date(lastEnd), 1);
        } else if (profile.occurrenceDefn?.initStartDt && profile.occurrenceDefn?.initEndDt) {
            eventStart = S25Util.date.parse(profile.occurrenceDefn.initStartDt);
            eventEnd = S25Util.date.parse(profile.occurrenceDefn.initEndDt);
        } else {
            eventStart = new Date();
            eventEnd = S25Util.date.addHours(eventStart, 1);
        }

        const newRsrvId = await ReservationService.generateReservationIds(1);
        const newOcc = {
            itemId: newRsrvId.idSet[0].rsrvId,
            locations: [],
            resources: [],
            profileId: profile.itemId,
            profileName: profile.itemName,
            state: 1,
            eventStart,
            eventEnd,
        } as S25Reservation;

        EventOccUtil.applyProfileAddedTime(newOcc, profile.occurrenceDefn?.addedTime);
        return newOcc;
    }

    private static applyProfileAddedTime(occ: S25Reservation, addTime?: AddedTime) {
        if (!addTime) return;

        if (addTime.preEvent) {
            occ.preEventStart = S25Util.date.parse(
                S25Util.date.addMinutes(occ.eventStart, -1 * S25Util.ISODurationToMinutes(addTime.preEvent)),
            );
        }

        if (addTime.postEvent) {
            occ.postEventEnd = S25Util.date.parse(
                S25Util.date.addMinutes(occ.eventEnd, S25Util.ISODurationToMinutes(addTime.postEvent)),
            );
        }

        if (addTime.setup) {
            const setupAnchor = occ.preEventStart ?? occ.eventStart;
            occ.setupStart = S25Util.date.parse(
                S25Util.date.addMinutes(setupAnchor, -1 * S25Util.ISODurationToMinutes(addTime.setup)),
            );
        }

        if (addTime.takedown) {
            const takedownAnchor = occ.postEventEnd ?? occ.eventEnd;
            occ.takeDownEnd = S25Util.date.parse(
                S25Util.date.addMinutes(takedownAnchor, S25Util.ISODurationToMinutes(addTime.takedown)),
            );
        }
    }

    //eventEnd: S25Util.date.parse(S25Util.date.copyTime(new Date(), occ.eventEnd)),
    //     public static changeActive(e: any, occ: S25Reservation) {
    //         console.log({ e: e });
    //         return occ;
    //     }

    //     public static delOcc(occ: S25Reservation) {
    //         return occ;
    //     }

    //     public static saveOcc(occ: S25Reservation) {
    //         return occ;
    //     }

    //delete and copy occurrance, date/tiem changed apply  to this function
    public static normalizeEventWSData(
        event: EventMicroI,
        occ: S25Reservation,
        isCopy?: boolean,
        isDelete?: boolean,
        isDateTimeChanged?: boolean,
        keepRepeatPattern?: boolean,
    ) {
        const locations = S25ProfileUtil.normalizeObjectsData(occ.locations, "location");
        const resources = S25ProfileUtil.normalizeObjectsData(occ.resources, "resource");

        const findProfile = event.profile.find((p: S25Profile) => {
            return p?.itemId === occ.profileId;
        });

        //if not copy and delete
        if (!isCopy && !isDelete) {
            findProfile.reservations = findProfile.reservations.map((rsrv: S25Reservation) => {
                if (rsrv.itemId === occ.itemId) {
                    return { ...rsrv, ...occ };
                }
                return rsrv;
            });
        }

        if (isDelete) {
            findProfile.reservations = findProfile.reservations.filter((item: S25Reservation) => {
                return item.itemId !== occ.itemId;
            });

            if (findProfile.reservations.length === 1) {
                findProfile.occurrenceDefn.recTypeId = RecordType.SingleDateTime;
                findProfile.occurrenceDefn.profileCode = this.setSingleProfieCode(
                    findProfile.reservations[0].eventStart as Date,
                );
            } else {
                findProfile.occurrenceDefn.recTypeId = RecordType.FreeForm;
            }
        }

        const isSingleOcc =
            findProfile.occurrenceDefn.recTypeId === RecordType.SingleDateTime || findProfile.reservations.length === 1;

        let reservations = S25ProfileUtil.normalizeRsrvWSData(
            findProfile.reservations,
            isSingleOcc ? RecordType.SingleDateTime : RecordType.FreeForm,
        );

        if (isCopy) {
            const generateNewOcc = {
                rsrvId: occ.itemId,
                comments: occ.comments,
                state: occ.state,
                occurrence: S25Util.date.toS25ISODateStr(occ.eventStart),
                rsrvStartDt:
                    S25Util.date.toS25ISODateTimeStr(occ.setupStart) ||
                    S25Util.date.toS25ISODateTimeStr(occ.preEventStart) ||
                    S25Util.date.toS25ISODateTimeStr(occ.eventStart),
                evStartDt: S25Util.date.toS25ISODateTimeStr(occ.eventStart),
                evEndDt: S25Util.date.toS25ISODateTimeStr(occ.eventEnd),
                rsrvEndDt:
                    S25Util.date.toS25ISODateTimeStr(occ.takeDownEnd) ||
                    S25Util.date.toS25ISODateTimeStr(occ.postEventEnd) ||
                    S25Util.date.toS25ISODateTimeStr(occ.eventEnd),
                spaces: locations,
                resources: resources,
            } as WSReservations;

            reservations.push(generateNewOcc); // ads hoc and RecurrenceGrammar payload need exising rsrvs otherwise service got no mpping rsrvs error

            if (!keepRepeatPattern) {
                findProfile.occurrenceDefn.recTypeId = RecordType.FreeForm;
                delete findProfile.occurrenceDefn?.adHocDates;
                if (findProfile?.occurrenceDefn?.profileCode) findProfile.occurrenceDefn.profileCode = "";
            }
        }

        const { initStartDt, initEndDt } = this.getFirstOccurrenceProfileDates(reservations);
        if (initStartDt && initEndDt) {
            findProfile.occurrenceDefn.initStartDt = initStartDt;
            findProfile.occurrenceDefn.initEndDt = initEndDt;
        }

        //  this occ to  changes  DateTime, just update this occ
        if (isDateTimeChanged && !(isCopy && keepRepeatPattern)) {
            if (isSingleOcc && !isCopy) {
                // single occ need to update occurrenceDefn
                findProfile.occurrenceDefn.recTypeId = RecordType.SingleDateTime;
                delete findProfile.occurrenceDefn?.adHocDates;
                delete reservations[0]?.rsrvStartDt; // ignored for SINGLE, AD-HOC, RECURRENCE profiles
                delete reservations[0]?.occurrence; // ignored for SINGLE, rsrvEndtDt values for each occurrence date

                findProfile.occurrenceDefn.profileCode = this.setSingleProfieCode(occ.eventStart as Date);
                findProfile.occurrenceDefn.initStartDt = S25Util.date.toS25ISODateTimeStr(occ.eventStart);
                findProfile.occurrenceDefn.initEndDt = S25Util.date.toS25ISODateTimeStr(occ.eventEnd);
            } else {
                findProfile.occurrenceDefn.recTypeId = RecordType.FreeForm;
                findProfile.occurrenceDefn.profileCode = "";
            }

            reservations = reservations.filter((rsrv) => {
                return rsrv.rsrvId === occ.itemId;
            });
        }

        const etags: Etags = { id: event.itemId, etag: event.etag };
        const data: EventDataMicroI = {
            items: [
                {
                    kind: "event",
                    id: event.itemId,
                    name: event.itemName,
                    isActive: event.isActive,
                    profiles: [
                        {
                            profileId: occ.profileId,
                            name: occ.profileName,
                            occurrenceDefn: findProfile?.occurrenceDefn,
                            reservations: reservations,
                        },
                    ], // profiles
                },
            ], // items
        };
        return [{ eTags: etags, data: data }];
    }

    /** Build micro save payload for copying an occurrence into one target segment. */
    public static normalizeCopyEventWSData(
        event: EventMicroI,
        occ: S25Reservation,
        target: CopyOccTarget | undefined,
        isDateTimeChanged?: boolean,
    ) {
        if (!target) {
            return EventOccUtil.normalizeEventWSData(event, occ, true, false, isDateTimeChanged, false);
        }
        const occForTarget = {
            ...occ,
            itemId: target.rsrvId,
            profileId: target.profileId,
            profileName: S25ProfileUtil.getProfileName(event.profile ?? [], target.profileId) ?? occ.profileName,
        } as S25Reservation;
        return EventOccUtil.normalizeEventWSData(
            event,
            occForTarget,
            true,
            false,
            isDateTimeChanged,
            target.keepRepeatPattern ?? false,
        );
    }

    // All space and resource reservations input with no label (draft,  request  & reserved)
    //// add Object apply to this function
    public static normalizeAddRsrvWSData(
        newItem: AvailLocationItems | AvailResourceItems | null,
        addedItems: any[],
        type: ObjectType,
        requestQty: number,
    ): ObjectSearchAvail.AddedItem[] {
        const results: ObjectSearchAvail.AddedItem[] = [];
        if (type === "location") {
            let share = newItem.always_shared === "T";
            if (newItem?.checkedObj?.conflicts) {
                share = newItem?.checkedObj?.conflicts.some((item: AvailLocationItems) => item.conflictShare === true);
            }

            if (newItem !== null) {
                const defaultLayout = S25Util.propertyGetParentWithChildValue(newItem?.layout, "default_layout", "T");
                results.push({
                    spaceId: S25Util.parseInt(newItem.space_id),
                    spaceName: newItem.space_name,
                    share: share,
                    instructions: newItem.instructions,
                    layouts: newItem?.layout,
                    maxCapacity: newItem.max_capacity,
                    ...(newItem?.checkedObj ? { checkedObj: newItem?.checkedObj } : {}),
                    // layoutId: defaultLayout?.layout_id || null,
                });
            }
        } else {
            if (newItem !== null) {
                const itemQuantity = typeof newItem.quantity === "number" ? newItem.quantity : undefined;
                results.push({
                    resourceId: S25Util.parseInt(newItem.resource_id),
                    resourceName: newItem.resource_name,
                    quantity: requestQty,
                    instructions: newItem.instructions,
                    ...(newItem?.checkedObj ? { checkedObj: newItem?.checkedObj } : {}),
                    ...(newItem?.allResAvailDateMap ? { allResAvailDateMap: newItem.allResAvailDateMap } : {}),
                    ...(itemQuantity != null ? { itemQuantity } : {}),
                });
            }
        }
        return results;
    }

    // All space and resource reservations input with no label (draft,  request  & reserved)
    // delete Object apply to this function
    public static normalizeDeleteRsrvWSData(
        removeItem: S25RmReservation | S25RsReservation | null | any,
        addItem: AvailLocationItems | AvailResourceItems | null,
        nodeName: string,
        occ: S25Reservation,
        type: ObjectType,
    ) {
        let results: any;
        const locations = S25ProfileUtil.normalizeObjectsData(occ.locations, "location");
        const resources = S25ProfileUtil.normalizeObjectsData(occ.resources, "resource");

        if (type === "location") {
            const dataset = {
                layoutId: removeItem?.layout?.itemId,
                instructions: removeItem.instructions,
                spaceId: removeItem.itemId,
            };
            results = { spaces: locations, remove: { spaces: [{ [nodeName]: [dataset] }] } };
        } else {
            const dataset = {
                instructions: removeItem.instructions,
                resourceId: removeItem.itemId,
            };
            results = { resources: resources, remove: { resources: [{ [nodeName]: [dataset] }] } };
        }
        return results;
    }

    public static getRepetitionText(profileCode: string) {
        const weekdayString = EventSummaryService.getWeekdayText(profileCode, false);
        return EventSummaryService.getRepetitionText(profileCode, null, null, null, weekdayString);
    }

    public static setSingleProfieCode(date: Date) {
        const dow = S25Util.date.parseDropTZ(date).getDay() as DowNumber;
        const profileCode =
            "W1 " +
            (BOARD_CONST.dowInt2Abbr[dow] ?? "") +
            " " +
            S25Util.date.toS25ISODateStr(date).replace(/-/g, "") +
            "T235900";
        return profileCode;
    }

    /**
     * finds the start/end date of the earliest reservation
     */
    public static getFirstOccurrenceProfileDates(rsrvs: WSReservations[]): {
        initStartDt?: string;
        initEndDt?: string;
    } {
        const firstOccurrence = rsrvs.reduce<WSReservations | undefined>((earliest, reservation) => {
            const startDt = reservation.evStartDt ?? reservation.rsrvStartDt;
            if (!startDt) {
                return earliest;
            }
            if (!earliest) {
                return reservation;
            }
            return EventOccUtil.compareOccurrenceOrder(reservation, earliest) < 0 ? reservation : earliest;
        }, undefined);

        if (!firstOccurrence) {
            return {};
        }

        const initStartDt = S25Util.date.toS25ISODateTimeStr(firstOccurrence.evStartDt ?? firstOccurrence.rsrvStartDt);
        const initEndDt = S25Util.date.toS25ISODateTimeStr(firstOccurrence.evEndDt ?? firstOccurrence.rsrvEndDt);

        return initStartDt && initEndDt ? { initStartDt, initEndDt } : {};
    }

    private static compareOccurrenceOrder(left: WSReservations, right: WSReservations): number {
        const leftStartMs = Date.parse(String(left.evStartDt ?? left.rsrvStartDt));
        const rightStartMs = Date.parse(String(right.evStartDt ?? right.rsrvStartDt));
        if (leftStartMs !== rightStartMs) {
            return leftStartMs - rightStartMs;
        }

        const leftEndMs = Date.parse(String(left.evEndDt ?? left.rsrvEndDt ?? left.evStartDt ?? left.rsrvStartDt));
        const rightEndMs = Date.parse(String(right.evEndDt ?? right.rsrvEndDt ?? right.evStartDt ?? right.rsrvStartDt));
        return leftEndMs - rightEndMs;
    }
}
